import MenuItem from 'flow/components/ui-components/MenuItem';
import NestedMenuItem from 'flow/components/ui-components/NestedMenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { showCallDialog } from 'flow/actions/call-dialog-actions';
import { useOutgoingCallsEnabled } from 'flow/hooks/api/useSoftphoneCallSettings';
import {
    getIsPhoneConnected,
    hasActiveCallsBlockingNewCall,
} from 'flow/selectors/call-selectors';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import {
    NewCallBlockReason,
    isRegisteredSIP,
    prettyFormatE164Number,
} from 'flow/utils/call-utils';
import NumberWrapper from '../ui-components/NumberWrapper';
import Tooltip from '../ui-components/Tooltip';
import { TypedMap } from 'flow/types/TypedMap';
import { Extension } from 'flow/types/Extension';
import useExtension from 'flow/hooks/useExtension';

import { Contact } from 'flow/types/Contact';
import useContact from 'flow/hooks/useContact';

interface Props {
    fixedNumber: string;
    technicalNumber: string;
    mobileNumber: string;
    extensionToCall: TypedMap<Extension> | TypedMap<Contact>;
    unknownContactNumber?: string;
    disableButton?: boolean;
    onMakeCallClicked?: () => void;
}

const CallButtonMenuItem = ({
    fixedNumber,
    technicalNumber,
    mobileNumber,
    extensionToCall,
    unknownContactNumber,
    disableButton = false,
    onMakeCallClicked,
}: Props) => {
    const { i18next, embedded } = useAppContext();
    const { ownExtension } = useExtension();
    const { ownContact } = useContact();
    const dispatch = useAppDispatch();

    const hasConnectedPhone = useAppSelector(getIsPhoneConnected);

    const ownCountryCode = useAppSelector(getOwnCountryCode);

    const hasCallBlockingNewCall = useAppSelector(
        (state) =>
            hasActiveCallsBlockingNewCall(state, 'outgoing') !==
            NewCallBlockReason.NONE,
    );

    const outgoingCallsEnabled = useOutgoingCallsEnabled();

    if (
        !fixedNumber &&
        !technicalNumber &&
        !mobileNumber &&
        !unknownContactNumber
    ) {
        return null;
    }

    if (isTeamsTypeIntegration(embedded)) {
        return null;
    }

    if (!hasConnectedPhone && !isRegisteredSIP(ownExtension)) {
        return null;
    }

    if (hasCallBlockingNewCall) {
        return null;
    }

    if (extensionToCall === ownExtension || extensionToCall === ownContact)
        return null;

    const isDisabled = disableButton || !outgoingCallsEnabled;

    const handleCall = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        numberToCall: string,
    ) => {
        event.stopPropagation();
        dispatch(showCallDialog(numberToCall, extensionToCall, undefined));

        if (onMakeCallClicked) {
            onMakeCallClicked();
        }
    };

    const disabledTooltipLabel = !outgoingCallsEnabled
        ? i18next.t('settings.softphone.outgoing_calls_disabled')
        : undefined;

    const wrapWithTooltip = (content: React.ReactNode) =>
        disabledTooltipLabel ? (
            <Tooltip label={disabledTooltipLabel} placement="top">
                {content}
            </Tooltip>
        ) : (
            <>{content}</>
        );

    if ((fixedNumber || technicalNumber) && mobileNumber) {
        return wrapWithTooltip(
            <NestedMenuItem title={i18next.t('Tooltip.Title.Call')}>
                <MenuItem
                    disabled={isDisabled}
                    onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                    ) => {
                        handleCall(event, fixedNumber || technicalNumber);
                    }}
                >
                    {fixedNumber ? (
                        <NumberWrapper>
                            {prettyFormatE164Number(
                                fixedNumber,
                                ownCountryCode,
                            )}
                        </NumberWrapper>
                    ) : (
                        i18next.t('context.menu.call_user')
                    )}
                </MenuItem>
                <MenuItem
                    disabled={isDisabled}
                    onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                    ) => {
                        handleCall(event, mobileNumber);
                    }}
                >
                    <NumberWrapper>
                        {prettyFormatE164Number(mobileNumber, ownCountryCode)}
                    </NumberWrapper>
                </MenuItem>
            </NestedMenuItem>,
        );
    } else {
        return wrapWithTooltip(
            <MenuItem
                disabled={isDisabled}
                onClick={(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                ) =>
                    handleCall(
                        event,
                        fixedNumber ||
                            technicalNumber ||
                            mobileNumber ||
                            unknownContactNumber,
                    )
                }
            >
                {i18next.t('Tooltip.Title.Call')}
            </MenuItem>,
        );
    }
};

export default CallButtonMenuItem;
